import { Button, Chip, Collapse, Grid, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import MessageOutlinedIcon from '@material-ui/icons/MessageOutlined';
import Authorize from 'components/Authorize';
import { isAuthorized } from 'components/Authorize/authorizeUtil';
import { permissionProfiles } from 'components/Authorize/permissionProfiles';
import EmptyPlaceholder from 'components/EmptyPlaceholder/EmptyPlaceholder';
import LoadingSpinner from 'components/LoadingSpinner';
import { userPreferencesComponentIds, userPreferencesTypes } from 'components/UserPreferences/userPreferencesConsts';
import { useGetUserPreferencesQuery, useUpdateUserPreferenceMutation } from 'components/UserPreferences/userPreferencesSlice';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import Note from './Note';
import NoteEditor from './NoteEditor';
import { HOT_NOTE_FILTER_KEY, NOTE_FILTERS, NoteEntityTypes } from './notesConsts';
import { useGetEntityNotesQuery } from './notesSlice';
import { NotesStyle } from './style';

const NoteList = ({
    notes,
    entityId,
    entityType,
    collapsible = true,
    readOnly = false,
    listOnly = false,
    title,
    style,
    showEmpty = false,
    editPermissionProfile = permissionProfiles.NOTES.EDIT_NOTES
}) => {
    const classes = NotesStyle();
    let { data: notesFromApi, error: notesError, isFetching: isLoadingNotes } = useGetEntityNotesQuery({ entityId: entityId, entityType: entityType }, { skip: !entityId || !!notes });

    const noteData = notes ?? notesFromApi ?? [];
    const [addNew, setAddNew] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    const availableFilters = NOTE_FILTERS.filter(f =>
        (!f.permission || isAuthorized(f.permission)) &&
        (!f.activityOnly || entityType === NoteEntityTypes.ACTIVITY)
    );

    const filterBarShown = availableFilters.length > 1;

    const { data: notesFilterPref } = useGetUserPreferencesQuery(
        { componentId: userPreferencesComponentIds.NOTES_FILTER, type: userPreferencesTypes.CUSTOM_VALUE, viewId: 0 },
        { skip: !filterBarShown }
    );
    const [updatePref] = useUpdateUserPreferenceMutation();

    const [deselectedKeys, setDeselectedKeys] = useState([]);
    const preferenceApplied = useRef(false);

    const savePreference = useMemo(() => debounce((keys) => {
        updatePref({
            componentId: userPreferencesComponentIds.NOTES_FILTER,
            type: userPreferencesTypes.CUSTOM_VALUE,
            customValue: JSON.stringify(keys)
        });
    }, 500), [updatePref]);

    useEffect(() => {
        if (preferenceApplied.current || !filterBarShown || notesFilterPref === undefined) return;
        preferenceApplied.current = true;
        try {
            const saved = JSON.parse(notesFilterPref?.customValue ?? 'null');
            if (Array.isArray(saved)) setDeselectedKeys(saved);
        } catch {
            // ignore malformed saved value — fall back to all-checked
        }
    }, [notesFilterPref, filterBarShown]);

    const activeFilters = availableFilters.filter(f => !deselectedKeys.includes(f.key));

    // With no visible filter bar (single available filter, e.g. vendors) the server's
    // visibility rules are the only authority — render every note it returned, since
    // client-facing notes may not match any bucket the user is allowed to see.
    const filteredNotes = filterBarShown ? noteData.filter(n => activeFilters.some(f => f.match(n))) : noteData;

    const toggleFilter = (key) => {
        preferenceApplied.current = true;
        setDeselectedKeys(prev => {
            const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
            savePreference(next);
            return next;
        });
    };

    const filterContent = (
        <Grid className={classes.noteListWrapper} container direction='column'>
            {availableFilters.length > 1 &&
                <Grid item container spacing={1} className={classes.noteFilterBar}>
                    {availableFilters.map(f => {
                        const selected = !deselectedKeys.includes(f.key);
                        const isHot = f.key === HOT_NOTE_FILTER_KEY;
                        const colorClass = isHot ? classes.noteFilterChipHot : classes.noteFilterChipDefault;
                        const selectedClass = selected
                            ? (isHot ? classes.noteFilterChipHotSelected : classes.noteFilterChipDefaultSelected)
                            : '';
                        return (
                            <Grid item key={f.key}>
                                <Chip
                                    label={f.label}
                                    size='small'
                                    clickable
                                    variant='outlined'
                                    icon={selected ? <CheckIcon /> : <AddIcon />}
                                    className={`${classes.noteFilterChip} ${colorClass} ${selectedClass}`}
                                    onClick={() => toggleFilter(f.key)}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            }
            <Grid container spacing={2} direction='column'>
                {filteredNotes.length > 0
                    ? filteredNotes.map((note, index) => (
                        <Grid container item key={note.id ?? index}>
                            <Note note={note} editPermissionProfile={editPermissionProfile} readOnly={readOnly} />
                        </Grid>
                    ))
                    : <EmptyPlaceholder text='No notes to display' />
                }
            </Grid>
        </Grid>
    );

    const getContainer = (content) => {
        if (collapsible) {
            return (
                <Collapse in={collapsed} timeout="auto" unmountOnExit>
                    {content}
                </Collapse>
            );
        }
        return content;
    };

    return (
        <>
            <Grid container direction='column' style={style ? style : { position: 'relative', minHeight: collapsible ? 0 : 30, padding: '10px' }}>
                <Grid item container justifyContent='space-between'>
                    {title && !!noteData?.length && <h4 className={classes.titleText}>{title}</h4>}
                    {!listOnly && <div>
                        <Grid container alignItems='center' className={classes.title} onClick={() => collapsible && setCollapsed(!collapsed)}>
                            <MessageOutlinedIcon className={classes.commentIcon} />
                            <div>{`Notes (${filteredNotes.length})`}</div>
                        </Grid>
                    </div>}
                    {!readOnly && <Authorize profile={editPermissionProfile} readonlyComponent={<></>}>
                        <Tooltip title='Add Note'>
                            <Button style={{ marginLeft: 'auto' }} onClick={() => setAddNew(true)} color="primary" variant='outlined'>
                                Add Note
                            </Button>
                        </Tooltip>
                    </Authorize>}
                </Grid>
                {getContainer(filterContent)}
            </Grid>
            {addNew && <NoteEditor note={{ entityId, entityType }} onClose={() => setAddNew(false)} />}
            <LoadingSpinner loading={isLoadingNotes} />
        </>
    );
}
export default NoteList;
